import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { 
  Users, 
  Check, 
  AlertTriangle, 
  AlertCircle,
  TrendingDown, 
  TrendingUp, 
  Minus,
  ChevronRight,
  Loader2,
  Search,
  RefreshCw,
  CheckCircle2,
  XCircle,
  Eye
} from 'lucide-react';
import { toast } from 'sonner';
import Navbar from '@/components/Navbar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function FridayBatchView({ user, onLogout }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [preview, setPreview] = useState(null);
  const [selectedYellow, setSelectedYellow] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [manualReviewModal, setManualReviewModal] = useState({ open: false, client: null });
  const [manualCalories, setManualCalories] = useState('');
  const [manualReason, setManualReason] = useState('');

  useEffect(() => {
    if (user?.role !== 'coach') {
      navigate('/app');
      return;
    }
    fetchPreview();
  }, [user, navigate]);

  const fetchPreview = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/traffic-light/preview`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPreview(response.data);
      
      // Default: Select all yellow zone clients
      const yellowIds = new Set(response.data.yellow?.map(c => c.client_id) || []);
      setSelectedYellow(yellowIds);
    } catch (error) {
      console.error('Failed to load traffic light preview:', error);
      toast.error('Failed to load weekly adjustments');
    } finally {
      setLoading(false);
    }
  };

  // Filter clients by search term
  const filteredYellow = useMemo(() => {
    if (!preview?.yellow) return [];
    if (!searchTerm) return preview.yellow;
    const term = searchTerm.toLowerCase();
    return preview.yellow.filter(c => 
      c.client_name?.toLowerCase().includes(term) ||
      c.email?.toLowerCase().includes(term)
    );
  }, [preview?.yellow, searchTerm]);

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedYellow(new Set(filteredYellow.map(c => c.client_id)));
    } else {
      setSelectedYellow(new Set());
    }
  };

  const handleToggleClient = (clientId) => {
    const newSet = new Set(selectedYellow);
    if (newSet.has(clientId)) {
      newSet.delete(clientId);
    } else {
      newSet.add(clientId);
    }
    setSelectedYellow(newSet);
  };

  const handleCommitBatch = async () => {
    if (selectedYellow.size === 0) {
      toast.error('No clients selected');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      
      // Get adjustments for selected clients
      const selectedAdjustments = preview.yellow.filter(
        c => selectedYellow.has(c.client_id)
      );

      const response = await axios.post(
        `${API}/traffic-light/approve-batch`,
        {
          client_ids: Array.from(selectedYellow),
          adjustments: selectedAdjustments
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(response.data.message);
      
      // Refresh the preview
      await fetchPreview();
    } catch (error) {
      console.error('Failed to commit batch:', error);
      toast.error('Failed to apply adjustments');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSendGreenNotifications = async () => {
    if (!preview?.green?.length) return;
    
    try {
      const token = localStorage.getItem('token');
      const greenIds = preview.green.map(c => c.client_id);
      
      const response = await axios.post(
        `${API}/traffic-light/send-green-notifications`,
        greenIds,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message);
    } catch (error) {
      console.error('Failed to send notifications:', error);
      toast.error('Failed to queue notifications');
    }
  };

  const handleManualOverride = async () => {
    if (!manualReviewModal.client || !manualCalories || !manualReason) {
      toast.error('Please fill in all fields');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      
      const response = await axios.post(
        `${API}/traffic-light/manual-override`,
        {
          client_id: manualReviewModal.client.client_id,
          new_calories: parseInt(manualCalories),
          reason: manualReason
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(response.data.message);
      setManualReviewModal({ open: false, client: null });
      setManualCalories('');
      setManualReason('');
      
      // Refresh
      await fetchPreview();
    } catch (error) {
      console.error('Failed to apply manual override:', error);
      toast.error('Failed to apply override');
    } finally {
      setSubmitting(false);
    }
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'INCREASE': return <TrendingUp className="w-4 h-4 text-green-500" />;
      case 'DECREASE': return <TrendingDown className="w-4 h-4 text-orange-500" />;
      case 'HOLD': return <Minus className="w-4 h-4 text-yellow-500" />;
      case 'NO_CHANGE': return <Check className="w-4 h-4 text-green-500" />;
      case 'MANUAL_REVIEW': return <AlertCircle className="w-4 h-4 text-red-500" />;
      default: return <Minus className="w-4 h-4 text-gray-400" />;
    }
  };

  const getActionBadge = (action, proposedCalories, currentCalories) => {
    const diff = proposedCalories - currentCalories;
    const diffPercent = currentCalories > 0 ? ((diff / currentCalories) * 100).toFixed(0) : 0;
    
    switch (action) {
      case 'INCREASE':
        return <Badge className="bg-green-100 text-green-700 hover:bg-green-100">+{diffPercent}% ↑</Badge>;
      case 'DECREASE':
        return <Badge className="bg-orange-100 text-orange-700 hover:bg-orange-100">{diffPercent}% ↓</Badge>;
      case 'HOLD':
        return <Badge className="bg-yellow-100 text-yellow-700 hover:bg-yellow-100">HOLD</Badge>;
      case 'NO_CHANGE':
        return <Badge className="bg-gray-100 text-gray-600 hover:bg-gray-100">No Change</Badge>;
      case 'MANUAL_REVIEW':
        return <Badge className="bg-red-100 text-red-700 hover:bg-red-100">Review</Badge>;
      default:
        return <Badge variant="outline">—</Badge>;
    }
  };

  if (!user || user.role !== 'coach') {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar user={user} onLogout={onLogout} />
      
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900" data-testid="friday-batch-title">
            Friday Batch View
          </h1>
          <p className="text-gray-600 mt-1">
            Review and approve weekly adjustments for all clients
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
          </div>
        ) : (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <Card className="border-l-4 border-l-green-500">
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Green Zone</p>
                      <p className="text-2xl font-bold text-green-600" data-testid="green-count">
                        {preview?.summary?.green_count || 0}
                      </p>
                    </div>
                    <CheckCircle2 className="w-8 h-8 text-green-500 opacity-50" />
                  </div>
                  <p className="text-xs text-gray-400 mt-1">Auto-pilot • On track</p>
                </CardContent>
              </Card>

              <Card className="border-l-4 border-l-yellow-500">
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Yellow Zone</p>
                      <p className="text-2xl font-bold text-yellow-600" data-testid="yellow-count">
                        {preview?.summary?.yellow_count || 0}
                      </p>
                    </div>
                    <AlertTriangle className="w-8 h-8 text-yellow-500 opacity-50" />
                  </div>
                  <p className="text-xs text-gray-400 mt-1">Batch approval needed</p>
                </CardContent>
              </Card>

              <Card className="border-l-4 border-l-red-500">
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Red Zone</p>
                      <p className="text-2xl font-bold text-red-600" data-testid="red-count">
                        {preview?.summary?.red_count || 0}
                      </p>
                    </div>
                    <XCircle className="w-8 h-8 text-red-500 opacity-50" />
                  </div>
                  <p className="text-xs text-gray-400 mt-1">Manual review required</p>
                </CardContent>
              </Card>

              <Card className="border-l-4 border-l-blue-500">
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Total Clients</p>
                      <p className="text-2xl font-bold text-blue-600" data-testid="total-count">
                        {preview?.summary?.total_clients || 0}
                      </p>
                    </div>
                    <Users className="w-8 h-8 text-blue-500 opacity-50" />
                  </div>
                  <p className="text-xs text-gray-400 mt-1">
                    Generated {preview?.summary?.generated_at ? new Date(preview.summary.generated_at).toLocaleTimeString() : 'now'}
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* RED ZONE - Manual Review (Top Priority) */}
            {preview?.red?.length > 0 && (
              <Card className="mb-6 border-red-200 bg-red-50/50">
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="w-5 h-5 text-red-500" />
                    <CardTitle className="text-lg text-red-700">
                      Red Zone - Manual Review Required ({preview.red.length})
                    </CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-red-600 mb-4">
                    These clients have unusual patterns requiring 1:1 attention. Click to review.
                  </p>
                  <div className="space-y-2">
                    {preview.red.map(client => (
                      <div 
                        key={client.client_id}
                        className="flex items-center justify-between p-3 bg-white rounded-lg border border-red-200 hover:border-red-400 cursor-pointer transition-colors"
                        onClick={() => {
                          setManualReviewModal({ open: true, client });
                          setManualCalories(client.current_calories?.toString() || '');
                        }}
                        data-testid={`red-client-${client.client_id}`}
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                            <AlertCircle className="w-5 h-5 text-red-500" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{client.client_name || client.email}</p>
                            <p className="text-sm text-red-600">{client.reason}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className="text-sm text-gray-500">
                              {client.weight_change_percent?.toFixed(2)}% change
                            </p>
                            <p className="text-sm font-medium">
                              {client.current_calories} kcal
                            </p>
                          </div>
                          <Button variant="outline" size="sm">
                            <Eye className="w-4 h-4 mr-1" /> Review
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* YELLOW ZONE - Batch Approval Queue */}
            <Card className="mb-6 border-yellow-200">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-yellow-500" />
                    <CardTitle className="text-lg">
                      Yellow Zone - Batch Approval ({preview?.yellow?.length || 0})
                    </CardTitle>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="relative">
                      <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <Input
                        placeholder="Search clients..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-9 w-48"
                        data-testid="yellow-search"
                      />
                    </div>
                    <Button variant="outline" size="sm" onClick={fetchPreview}>
                      <RefreshCw className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {filteredYellow.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">
                    {preview?.yellow?.length === 0 
                      ? 'No clients need attention this week 🎉' 
                      : 'No matching clients'}
                  </p>
                ) : (
                  <>
                    {/* Select All Header */}
                    <div className="flex items-center gap-3 pb-3 mb-3 border-b bg-gray-50 -mx-6 px-6 -mt-2 pt-2">
                      <Checkbox
                        checked={selectedYellow.size === filteredYellow.length && filteredYellow.length > 0}
                        onCheckedChange={handleSelectAll}
                        data-testid="select-all-yellow"
                      />
                      <span className="text-sm font-medium text-gray-600">
                        {selectedYellow.size} of {filteredYellow.length} selected
                      </span>
                    </div>

                    {/* High-density table */}
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="w-10 px-2 py-2"></th>
                            <th className="text-left px-2 py-2 font-medium text-gray-600">Client</th>
                            <th className="text-center px-2 py-2 font-medium text-gray-600">Weight Δ</th>
                            <th className="text-center px-2 py-2 font-medium text-gray-600">Adherence</th>
                            <th className="text-center px-2 py-2 font-medium text-gray-600">Plateau</th>
                            <th className="text-center px-2 py-2 font-medium text-gray-600">Action</th>
                            <th className="text-right px-2 py-2 font-medium text-gray-600">Calories</th>
                            <th className="text-left px-2 py-2 font-medium text-gray-600">Reason</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredYellow.map((client, index) => (
                            <tr 
                              key={client.client_id}
                              className={`border-b hover:bg-yellow-50/50 transition-colors ${
                                index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'
                              }`}
                              data-testid={`yellow-row-${client.client_id}`}
                            >
                              <td className="px-2 py-2">
                                <Checkbox
                                  checked={selectedYellow.has(client.client_id)}
                                  onCheckedChange={() => handleToggleClient(client.client_id)}
                                  data-testid={`checkbox-${client.client_id}`}
                                />
                              </td>
                              <td className="px-2 py-2">
                                <div>
                                  <p className="font-medium text-gray-900 truncate max-w-[150px]">
                                    {client.client_name || 'Unknown'}
                                  </p>
                                  <p className="text-xs text-gray-500 truncate max-w-[150px]">
                                    {client.email}
                                  </p>
                                </div>
                              </td>
                              <td className="px-2 py-2 text-center">
                                <span className={`font-mono ${
                                  client.weight_change_percent > 0 ? 'text-green-600' : 
                                  client.weight_change_percent < 0 ? 'text-red-600' : 'text-gray-600'
                                }`}>
                                  {client.weight_change_percent > 0 ? '+' : ''}{client.weight_change_percent?.toFixed(2)}%
                                </span>
                              </td>
                              <td className="px-2 py-2 text-center">
                                <span className={`font-mono ${
                                  client.adherence >= 80 ? 'text-green-600' :
                                  client.adherence >= 60 ? 'text-yellow-600' : 'text-red-600'
                                }`}>
                                  {client.adherence?.toFixed(0)}%
                                </span>
                              </td>
                              <td className="px-2 py-2 text-center">
                                {client.plateau_weeks > 0 ? (
                                  <Badge variant="outline" className="text-xs">
                                    {client.plateau_weeks}w
                                  </Badge>
                                ) : (
                                  <span className="text-gray-400">—</span>
                                )}
                              </td>
                              <td className="px-2 py-2 text-center">
                                <div className="flex items-center justify-center gap-1">
                                  {getActionIcon(client.proposed_action)}
                                  {getActionBadge(client.proposed_action, client.proposed_calories, client.current_calories)}
                                </div>
                              </td>
                              <td className="px-2 py-2 text-right">
                                <div className="text-xs">
                                  <span className="text-gray-400">{client.current_calories}</span>
                                  {client.proposed_action !== 'HOLD' && client.proposed_action !== 'NO_CHANGE' && (
                                    <>
                                      <span className="mx-1">→</span>
                                      <span className="font-medium">{client.proposed_calories}</span>
                                    </>
                                  )}
                                </div>
                              </td>
                              <td className="px-2 py-2">
                                <p className="text-xs text-gray-600 truncate max-w-[200px]" title={client.reason}>
                                  {client.reason}
                                </p>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* GREEN ZONE - Auto-pilot */}
            <Card className="mb-24 border-green-200 bg-green-50/30">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-green-500" />
                    <CardTitle className="text-lg text-green-700">
                      Green Zone - On Track ({preview?.green?.length || 0})
                    </CardTitle>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleSendGreenNotifications}
                    disabled={!preview?.green?.length}
                    className="text-green-600 border-green-300 hover:bg-green-50"
                  >
                    <Check className="w-4 h-4 mr-1" /> Send "Good Job" Emails
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {preview?.green?.length === 0 ? (
                  <p className="text-center text-gray-500 py-4">No clients in green zone</p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                    {preview?.green?.slice(0, 12).map(client => (
                      <div 
                        key={client.client_id}
                        className="flex items-center gap-2 p-2 bg-white rounded border border-green-100"
                        data-testid={`green-client-${client.client_id}`}
                      >
                        <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {client.client_name || client.email}
                          </p>
                          <p className="text-xs text-green-600">
                            {client.weight_change_percent?.toFixed(2)}% • {client.current_calories} kcal
                          </p>
                        </div>
                      </div>
                    ))}
                    {preview?.green?.length > 12 && (
                      <div className="flex items-center justify-center p-2 text-sm text-gray-500">
                        +{preview.green.length - 12} more
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Sticky Footer Action Bar */}
            <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg z-50">
              <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  <span className="font-medium">{selectedYellow.size}</span> clients selected for batch approval
                </div>
                <Button
                  size="lg"
                  onClick={handleCommitBatch}
                  disabled={selectedYellow.size === 0 || submitting}
                  className="bg-yellow-500 hover:bg-yellow-600 text-white px-8"
                  data-testid="commit-batch-btn"
                >
                  {submitting ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Check className="w-4 h-4 mr-2" />
                  )}
                  Commit Batch Adjustments ({selectedYellow.size})
                </Button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Manual Review Modal */}
      <Dialog open={manualReviewModal.open} onOpenChange={(open) => !open && setManualReviewModal({ open: false, client: null })}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle className="w-5 h-5" />
              Manual Review: {manualReviewModal.client?.client_name}
            </DialogTitle>
          </DialogHeader>
          
          {manualReviewModal.client && (
            <div className="space-y-4">
              <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                <p className="text-sm font-medium text-red-700 mb-1">Issue:</p>
                <p className="text-sm text-red-600">{manualReviewModal.client.reason}</p>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Current Weight</p>
                  <p className="font-medium">{manualReviewModal.client.current_weight?.toFixed(1)} kg</p>
                </div>
                <div>
                  <p className="text-gray-500">Weight Change</p>
                  <p className={`font-medium ${
                    manualReviewModal.client.weight_change_percent > 0 ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {manualReviewModal.client.weight_change_percent?.toFixed(2)}%
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Current Calories</p>
                  <p className="font-medium">{manualReviewModal.client.current_calories} kcal</p>
                </div>
                <div>
                  <p className="text-gray-500">Adherence</p>
                  <p className="font-medium">{manualReviewModal.client.adherence?.toFixed(0)}%</p>
                </div>
              </div>

              <div className="space-y-3 pt-4 border-t">
                <div>
                  <label className="text-sm font-medium text-gray-700">New Calorie Target</label>
                  <Input
                    type="number"
                    value={manualCalories}
                    onChange={(e) => setManualCalories(e.target.value)}
                    placeholder="Enter new calories"
                    className="mt-1"
                    data-testid="manual-calories-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Reason for Override</label>
                  <Input
                    value={manualReason}
                    onChange={(e) => setManualReason(e.target.value)}
                    placeholder="e.g., Suspected water retention, needs follow-up call"
                    className="mt-1"
                    data-testid="manual-reason-input"
                  />
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setManualReviewModal({ open: false, client: null })}>
              Cancel
            </Button>
            <Button 
              onClick={handleManualOverride}
              disabled={submitting || !manualCalories || !manualReason}
              className="bg-red-600 hover:bg-red-700"
              data-testid="apply-override-btn"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Apply Override
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
